import { createColumnHelper } from '@tanstack/react-table';
import { CircleDollarSign, CloudCog, Info, Pencil, Trash2 } from 'lucide-react';

import {
  ScrollableCell,
  ScrollableCellWithAvatar,
} from '@/components/data-table/cells/scrollable-cell';
import { Button } from '@/components/ui/button';
import i18n from '@/core/i18n';
import { formatTableDate } from '@/lib/constants/table-display';
import { FeatureMode } from '@/modules/common/components/templates';
import { AllowedAction } from '@/modules/common/types/allowed-actions';

import { ChangedFieldsCell } from '../components/changed-fields/changed-fields-cell';
import { ProjectChangeLogEntry, ProjectChangeType } from '../types/project-changes';

const columnHelper = createColumnHelper<ProjectChangeLogEntry>();

export const getChangesTypeMap = () => ({
  [ProjectChangeType.Info]: {
    icon: <Info size={18} />,
    label: i18n.t('authenticated.projects:details.tabs.changes.info'),
  },
  [ProjectChangeType.Billing]: {
    icon: <CircleDollarSign size={18} />,
    label: i18n.t('authenticated.projects:details.tabs.changes.billing'),
  },
  [ProjectChangeType.Integration]: {
    icon: <CloudCog size={18} />,
    label: i18n.t('authenticated.projects:details.tabs.changes.integration'),
  },
});

export const getProjectChangesColumns = ({
  handleEditClick,
  handleDeleteClick,
  isEditProjectEnabled,
  isEditProjectBillingEnabled,
  mode,
}: {
  handleEditClick: (row: ProjectChangeLogEntry) => void;
  handleDeleteClick: (row: ProjectChangeLogEntry) => void;
  mode?: FeatureMode;
  isEditProjectEnabled: boolean;
  isEditProjectBillingEnabled: boolean;
}) => [
  columnHelper.accessor('type', {
    header: i18n.t('authenticated.projects:details.tabs.changes.table.type'),
    size: 100,
    minSize: 100,
    cell: ({ getValue }) => {
      const typeMapper = getChangesTypeMap();
      const typeData = typeMapper[getValue() as ProjectChangeType];
      return (
        <div className="flex min-w-0 items-center gap-2">
          <div className="shrink-0">{typeData.icon}</div>
          <ScrollableCell text={typeData.label} />
        </div>
      );
    },
  }),
  columnHelper.accessor('startDate', {
    header: i18n.t('authenticated.projects:details.tabs.changes.table.startDate'),
    minSize: 100,
    size: 100,
    cell: ({ getValue }) => formatTableDate(getValue()),
  }),
  columnHelper.accessor('endDate', {
    header: i18n.t('authenticated.projects:details.tabs.changes.table.endDate'),
    minSize: 100,
    size: 100,
    cell: ({ getValue }) => formatTableDate(getValue()),
  }),
  columnHelper.accessor('changedBy', {
    header: i18n.t('authenticated.projects:details.tabs.changes.table.changedBy'),
    cell: ({ getValue }) => {
      const changedBy = getValue();
      return (
        <ScrollableCellWithAvatar
          text={changedBy.name}
          avatarName={changedBy.name}
          image={changedBy.profileImageUrl ?? undefined}
        />
      );
    },
  }),
  columnHelper.accessor('changedFields', {
    header: () => (
      <div className="pl-4">
        {i18n.t('authenticated.projects:details.tabs.changes.table.changedFields')}
      </div>
    ),
    size: 350,
    minSize: 300,
    cell: ({ getValue }) => <ChangedFieldsCell changedFields={getValue()} />,
  }),
  columnHelper.display({
    id: 'actions',
    minSize: 100,
    header: () => (
      <div className="w-full text-center">
        {i18n.t('authenticated.projects:details.tabs.changes.table.action')}
      </div>
    ),
    cell: ({ row }) => {
      const allowedActions = row.original.allowedActions;
      const type = row.original.type;
      const canEdit = allowedActions.includes(AllowedAction.EDIT);
      const canDelete = allowedActions.includes(AllowedAction.DELETE);
      // Archived projects must still allow editing/deleting changes that were
      // made before archivation (OPS-1519). Gating is handled by permissions
      // (isModificationAllowed) + per-row allowedActions, so the only blocker
      // here is read-only mode.
      const isDisabled = mode === FeatureMode.Read;

      const isModificationAllowed =
        isEditProjectEnabled && (type != ProjectChangeType.Billing || isEditProjectBillingEnabled);

      return (
        <div className="flex items-center justify-end">
          {canEdit && isModificationAllowed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleEditClick(row.original)}
              disabled={isDisabled}
            >
              <Pencil />
            </Button>
          )}
          {canDelete && isModificationAllowed && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDeleteClick(row.original)}
              disabled={isDisabled}
            >
              <Trash2 />
            </Button>
          )}
        </div>
      );
    },
  }),
];
